import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faCircleInfo, faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filterUndefineds } from '@olivierpascal/helpers';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import type {
  IArticleFormSubmitAction,
  IArticleFormValues,
} from './ArticleForm';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { Callout } from '~/components/molecules/Callout';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { getActionTypeFromTarget } from '~/helpers/getActionTypeFromTarget';
import { getCategoryActionFromType } from '~/helpers/getCategoryActionFromType';
import { useArticleDraftQuery } from '~/lib/api/modules/Articles/hooks/useArticleDraftQuery';
import { useCreateArticleDraftMutation } from '~/lib/api/modules/Articles/hooks/useCreateArticleDraftMutation';
import { usePublishArticleMutation } from '~/lib/api/modules/Articles/hooks/usePublishArticleMutation';
import { useUnpublishArticleMutation } from '~/lib/api/modules/Articles/hooks/useUnpublishArticleMutation';
import { ArticleForm } from './ArticleForm';

export type IUpdateArticleFormProps = IContainerProps & {
  onSuccess?: (outputData: unknown, inputValues: IArticleFormValues) => void;
  cancelUrl?: string;
  appId: string;
  articleId: string;
  onArticleChange?: (article: IApiGetArticleResponseBody) => void;
};

export const UpdateArticleForm: React.FC<IUpdateArticleFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, articleId, onArticleChange } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [submittingAction, setSubmittingAction] = useState<
    IArticleFormSubmitAction | undefined
  >();
  const articleDraftQuery = useArticleDraftQuery(
    {
      appId,
      articleId,
    },
    {
      cacheTime: 0,
      staleTime: Infinity,
    },
  );
  const createArticleDraftMutation = useCreateArticleDraftMutation();
  const publishArticleDraftMutation = usePublishArticleMutation();
  const unpublishArticleMutation = useUnpublishArticleMutation();

  const handleSubmit: SubmitHandler<IArticleFormValues> = async (
    values: IArticleFormValues,
    _?: React.BaseSyntheticEvent,
    action?: IArticleFormSubmitAction,
  ): Promise<void> => {
    if (submitting) {
      return;
    }

    setSubmittingAction(action);

    const actions = values.actionButtonEnabled
      ? [
          {
            title: values.actionButtonLabel ?? '',
            url: `${values.actionButtonPrefix ?? ''}${values.actionButtonTarget ?? ''}`,
          },
        ]
      : undefined;

    const badges = values.userBadges?.map((id) => id);
    const badgesAllow = values.userBadges?.length
      ? values.requireAllUserBadges
        ? 'all'
        : 'any'
      : undefined;

    const mainPictureIds = values.mainMedias
      ?.filter(({ type }) => type === 'picture')
      .map(({ id }) => id);
    const galleryPictureIds = values.galleryPictures?.map(({ id }) => id);
    const pictureIds = [
      ...(mainPictureIds ?? []),
      ...(galleryPictureIds ?? []),
    ];

    const mainVideoIds = values.mainMedias
      ?.filter(({ type }) => type === 'video')
      .map(({ id }) => id);
    const videoIds = mainVideoIds ?? [];

    const documentIds = values.documents?.map(({ id }) => id);

    return createArticleDraftMutation
      .mutateAsync({
        appId,
        articleId,
        body: {
          actions,
          badges,
          badgesAllow,
          categoriesId: values.categoryIds,
          eventEndDate: values.eventEndDate,
          eventStartDate: values.eventStartDate,
          html: values.html,
          isPoll: values.type === 'poll',
          isEvent: values.isEvent,
          isWebview: values.type === 'webview',
          likes: data?.likes,
          pdfsOpenButton: values.documentsButtonLabel,
          mediaCaptions: values.mainMediaCaption,
          md: values.markdown ?? '',
          pdfs: documentIds,
          pictures: pictureIds,
          feedPicture: values.feedPictures?.[0]?.id,
          pinned: values.pinned,
          productId: values.productId,
          summary: data?.summary ?? ' ',
          title: values.title ?? '',
          videoPlayMode: data?.videoPlayMode,
          videos: videoIds,
          views: data?.views,
          hideFromFeed: values.hideFromFeed,
          displayOptions: {
            ...data?.displayOptions,
            hideComments: values.hideComments,
            hideSocial: values.hideShareButtons,
            fullscreen: values.fullscreen,
            rawContent: values.rawContent,
          },
        },
      })
      .then(async (data) => {
        if (action === 'publish' || action === 'publishAndNotify') {
          await publishArticleDraftMutation.mutateAsync({
            appId,
            articleId,
            body: {
              draftId: data.draftId,
              date: values.publishAt ?? new Date().toISOString(),
              unpublicationDate: values.unpublishAt,
              ...(action === 'publishAndNotify'
                ? {
                    sendNotifications: true,
                    notificationTitle: values.notificationTitle,
                    notificationContent: values.notificationBody,
                  }
                : undefined),
            },
          });
        }
        if (action === 'unpublish') {
          await unpublishArticleMutation.mutateAsync({
            appId,
            articleId,
          });
        }
      })
      .then(() => onSuccess?.(undefined, values))
      .finally(() => setSubmittingAction(undefined));
  };

  const submitting =
    createArticleDraftMutation.isLoading ||
    publishArticleDraftMutation.isLoading ||
    unpublishArticleMutation.isLoading;
  const data = articleDraftQuery.data;

  const actionValue = data?.actions?.length ? data?.actions[0].url : undefined;
  const actionType = actionValue
    ? getActionTypeFromTarget(actionValue)
    : undefined;
  const action = actionType
    ? getCategoryActionFromType(actionType, t)
    : undefined;
  const actionTarget = actionValue?.replace(action?.prefix ?? '', '');

  const now = DateTime.now();
  const publicationDate = data?.publicationDate
    ? DateTime.fromISO(data.publicationDate)
    : undefined;
  const unpublicationDate = data?.unpublicationDate
    ? DateTime.fromISO(data.unpublicationDate)
    : undefined;

  const publicationScheduled = !!publicationDate && publicationDate > now;
  const unpublicationScheduled = !!unpublicationDate && unpublicationDate > now;

  const unpublished =
    !data?.isPublished || (!!unpublicationDate && unpublicationDate <= now);
  const published = !unpublished && publicationDate && publicationDate <= now;
  const isDraft = !!data && !published;
  const publishAt =
    !isDraft || publicationScheduled
      ? (publicationDate ?? undefined)
      : undefined;
  const unpublishAt =
    !isDraft || unpublicationScheduled
      ? (unpublicationDate ?? undefined)
      : undefined;
  const canSchedulePublication = !published || (!!publishAt && publishAt > now);

  return (
    <ContentBlock draft={isDraft} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('UpdateArticleForm.update-an-article')}{' '}
        {isDraft ? t('UpdateArticleForm.draft') : null}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={articleDraftQuery.isLoading}
        error={articleDraftQuery.error}
      >
        {data ? (
          <ArticleForm
            mode="edition"
            banners={filterUndefineds([
              publicationScheduled ? (
                <Callout key={0} icon={<FontAwesomeIcon icon={faCircleInfo} />}>
                  {t('UpdateArticleForm.this-article-is')}{' '}
                  <strong>
                    {t('UpdateArticleForm.scheduled-for-publication')}
                  </strong>{' '}
                  {t('UpdateArticleForm.on')}{' '}
                  {publicationDate.toLocaleString(DateTime.DATETIME_SHORT)} (
                  <RelativeTime date={publicationDate} />
                  ).
                </Callout>
              ) : undefined,
              unpublicationScheduled ? (
                <Callout key={1} icon={<FontAwesomeIcon icon={faCircleInfo} />}>
                  {t('UpdateArticleForm.this-article-is')}{' '}
                  <strong>
                    {t('UpdateArticleForm.scheduled-for-depublication')}
                  </strong>{' '}
                  {t('UpdateArticleForm.on')}{' '}
                  {unpublicationDate.toLocaleString(DateTime.DATETIME_SHORT, {
                    locale,
                  })}{' '}
                  (<RelativeTime date={unpublicationDate} />
                  ).
                </Callout>
              ) : undefined,
            ])}
            appId={appId}
            cancelUrl={cancelUrl}
            defaultValues={{
              type: data.isWebview
                ? 'webview'
                : data.isPoll
                  ? 'poll'
                  : 'article',
              title: data.title,
              markdown: data.md,
              categoryIds: data.categoriesId,
              mainMedias: data.videos?.length
                ? [{ type: 'video', id: data.videos[0] }]
                : data.pictures?.length
                  ? [{ type: 'picture', id: data.pictures[0] }]
                  : undefined,
              mainMediaCaption: data.mediaCaptions,
              actionButtonEnabled: !!data.actions?.length,
              actionButtonType: actionType,
              actionButtonPrefix: action?.prefix,
              actionButtonTarget: actionTarget,
              actionButtonLabel: data.actions?.length
                ? data.actions[0].title
                : undefined,
              galleryEnabled:
                (data.videos?.length ?? 0) + (data.pictures?.length ?? 0) > 1,
              galleryPictures: data.pictures
                ?.slice(data.videos?.length ? 0 : 1)
                .map((id) => ({
                  type: 'picture',
                  id,
                })),
              feedPictures: data.feedPicture
                ? [
                    {
                      type: 'picture',
                      id: data.feedPicture,
                    },
                  ]
                : undefined,
              documentsEnabled: !!data.pdfs?.length,
              documents: data.pdfs?.map((id) => ({ type: 'document', id })),
              documentsButtonLabel: data.pdfsOpenButton,
              userBadges: data.badges?.list?.length
                ? data.badges.list.map(({ id }) => id)
                : undefined,
              requireAllUserBadges: !!(
                data.badges?.list?.length && data.badges?.allow === 'all'
              ),
              pinned: data.pinned,
              publicationScheduled: !!publishAt,
              publishAt: publishAt?.toISO() ?? undefined,
              unpublicationScheduled: !!unpublishAt,
              unpublishAt: unpublishAt?.toISO() ?? undefined,
              isPublished: published,
              productId: data.storeProductId,
              isEvent: data.isEvent,
              eventStartDate: data.eventStartDate,
              eventEndDate: data.eventEndDate,
              hideFromFeed: data.hideFromFeed,
              hideComments: data.displayOptions?.hideComments,
              hideShareButtons: data.displayOptions?.hideSocial,
              fullscreen: data.displayOptions?.fullscreen,
              rawContent: data.displayOptions?.rawContent,
            }}
            onSubmit={handleSubmit}
            submittingAction={submittingAction}
            onArticleChange={onArticleChange}
            canSchedulePublication={canSchedulePublication}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
